import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import HistoryPicker from '../components/HistoryPicker';
import ChecklistAdmin from './ChecklistAdmin';
import HistoryTagPicker from '../components/HistoryTagPicker';

function getLocalDateStr(d = new Date()) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}


const WEEKDAYS = [
  { value: 1, label: '周一' }, { value: 2, label: '周二' },
  { value: 3, label: '周三' }, { value: 4, label: '周四' },
  { value: 5, label: '周五' }, { value: 6, label: '周六' },
  { value: 7, label: '周日' },
];

// 巡检地点列表
const INSPECT_LOCATIONS = [
  '1幢1-33楼', '2幢1-33楼', '3幢1-33楼', '5幢1-33楼', '6幢1-33楼',
  '各幢架空层', '一楼园区', '地下室车库',
  '排风送风机房', '发电机房', '楼顶',
  '会所二楼', '会所健身房', '小区外围'
];

const TASK_TYPES = ['普通任务', '巡检'];

const STATUS_COLORS = { '待完成': '#1890ff', '已完成': '#52c41a', '已超时': '#ff4d4f' };

const DEPARTMENTS = [
  '项目管理部',
  '客户服务部',
  '工程维修部',
  '秩序维护部',
  '消防监控部',
  '环境管理部',
  '财务部'
];

const ROLE_DEPARTMENT = {
  general_manager: '项目管理部',
  manager: '项目管理部',
  steward_chief: '客户服务部',
  steward: '客户服务部',
  engineer: '工程维修部',
  security: '秩序维护部',
  order_staff: '秩序维护部',
  fire_control: '消防监控部',
  cleaner_chief: '环境管理部',
  cleaner: '环境管理部',
  finance: '财务部'
};

const DEPARTMENT_ALIASES = {
  '项目管理部': ['项目管理部'],
  '客户服务部': ['客户服务部', '管家部'],
  '工程维修部': ['工程维修部', '工程部', '秩序工程部'],
  '秩序维护部': ['秩序维护部', '秩序部', '秩序工程部'],
  '消防监控部': ['消防监控部', '秩序部', '秩序工程部'],
  '环境管理部': ['环境管理部', '保洁部', '绿化部'],
  '财务部': ['财务部']
};

const departmentMatches = (role, department) => {
  const standard = ROLE_DEPARTMENT[role];
  if (!standard) return false;
  return (DEPARTMENT_ALIASES[standard] || [standard]).includes(department);
};

function CnInput({ value, onChange, placeholder, style }) {
  const composing = useRef(false);
  const [local, setLocal] = useState(value || '');
  useEffect(() => { if (!composing.current) setLocal(value || ''); }, [value]);
  return (
    <input value={local} placeholder={placeholder} style={style}
      onChange={e => { setLocal(e.target.value); if (!composing.current) onChange(e.target.value); }}
      onCompositionStart={() => { composing.current = true; }}
      onCompositionEnd={e => { composing.current = false; setLocal(e.target.value); onChange(e.target.value); }} />
  );
}

export default function DailyTask({ user }) {
  const navigate = useNavigate();
  const isManager = ['admin', 'manager', 'general_manager', 'steward'].includes(user.role);
  const canViewHistory = ['admin', 'manager', 'general_manager'].includes(user.role);
  const [tab, setTab] = useState('today');
  const [tasks, setTasks] = useState([]);
  const [history, setHistory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getLocalDateStr());
  const [showModal, setShowModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedWeekdays, setSelectedWeekdays] = useState([1,2,3,4,5]);
  const [taskType, setTaskType] = useState('普通任务');
  const [inspectLocation, setInspectLocation] = useState('');
  const [inspectItems, setInspectItems] = useState('');
  const [checklistTemplates, setChecklistTemplates] = useState([]);
  const captureInputRef = useRef(null);
  const captureTaskRef = useRef(null);
  const [photoProcessing, setPhotoProcessing] = useState(false);
  const [, setClockTick] = useState(0);

  // 每30秒触发一次重新渲染，使任务到开放时间后自动解锁
  useEffect(() => {
    const timer = setInterval(() => {
      setClockTick(v => v + 1);
    }, 30000);

    return () => clearInterval(timer);
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const res = await API.get('/daily', { params: { date: selectedDate } });
      setTasks(res.data);
    } catch (err) { alert('获取失败'); }
    finally { setLoading(false); }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await API.get('/daily/history', {
        params: { date: selectedDate, limit: 500 }
      });
      setHistory(res.data);
    } catch (err) {
      alert(err.response?.data?.message || '获取完成记录失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await API.get('/daily/templates');
      setTemplates(res.data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (tab === 'today') {
      fetchTasks();
    } else if (tab === 'history') {
      if (canViewHistory) fetchHistory();
    } else {
      fetchTemplates();
      API.get('/users')
        .then(r => setUsers(r.data.filter(u => u.is_active && !['owner','committee'].includes(u.role))))
        .catch(()=>{});
      API.get('/checklist/templates')
        .then(r => setChecklistTemplates(r.data))
        .catch(()=>{});
    }
  }, [tab, selectedDate]);

  const toggleWeekday = (d) => setSelectedWeekdays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());

  const handleAddTemplate = async () => {
    if (taskType === '巡检' && !form.checklist_template_id) { alert('请选择巡检清单模板'); return; }
    const tpl = checklistTemplates.find(t => String(t.id) === String(form.checklist_template_id));
    const title = taskType === '巡检' ? `巡检 - ${tpl ? tpl.location : inspectLocation}` : form.title;
    if (!title || !form.scheduled_time || selectedWeekdays.length === 0) { alert('请填写完整信息'); return; }
    if (taskType === '巡检' && form.scheduled_time_end && form.scheduled_time_end <= form.scheduled_time) { alert('结束时间需晚于开始时间'); return; }
    if (!form.assign_type) { alert('请选择分派方式'); return; }
    try {
      const payload = { ...form, title, weekdays: selectedWeekdays.join(','), task_type: taskType, inspect_location: taskType === '巡检' ? inspectLocation : '', inspect_items: taskType === '巡检' ? inspectItems : '', checklist_template_id: taskType === '巡检' && form.checklist_template_id ? parseInt(form.checklist_template_id) : null };
      if (selected) await API.put(`/daily/templates/${selected.id}`, { ...payload, is_active: 1 });
      else await API.post('/daily/templates', payload);
      alert(selected ? '修改成功' : '创建成功');
      setShowModal(null);
      fetchTemplates();
    } catch (err) { alert('操作失败'); }
  };

  const handleComplete = async () => {
    if (!image) { alert('请拍照上传完成照片'); return; }
    try {
      const data = new FormData();
      data.append('image', image);
      if (form.note) data.append('note', form.note);
      await API.put(`/daily/${selected.id}/complete`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert('完成确认成功');
      setShowModal(null);
      setImage(null); setPreview(null);
      fetchTasks();
    } catch (err) { alert(err.response?.data?.message || '操作失败'); }
  };

  const getGpsLocation = () => new Promise(resolve => {
    if (!navigator.geolocation || !window.isSecureContext) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
  });

  const watermarkTaskPhoto = async (file, task) => {
    const gps = await getGpsLocation();
    const url = URL.createObjectURL(file);
    const photo = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
    const maxSide = 1600;
    const scale = Math.min(1, maxSide / Math.max(photo.naturalWidth, photo.naturalHeight));
    const width = Math.max(1, Math.round(photo.naturalWidth * scale));
    const height = Math.max(1, Math.round(photo.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(photo, 0, 0, width, height);
    URL.revokeObjectURL(url);

    const now = new Date();
    const timeText = now.toLocaleString('zh-CN', { hour12: false }).replace(/\//g, '-');
    const taskLocation = task?.inspect_location || task?.title || '任务现场';
    const lines = [
      `拍摄时间：${timeText}`,
      '项目位置：永嘉县 · 大诚瓯江印象',
      `任务位置：${taskLocation}`
    ];
    if (gps) lines.push(`GPS：${gps.lat.toFixed(6)}, ${gps.lng.toFixed(6)}`);

    const fontSize = Math.max(22, Math.round(width * 0.025));
    const lineHeight = Math.round(fontSize * 1.45);
    const padding = Math.max(18, Math.round(width * 0.022));
    const boxHeight = padding * 2 + lineHeight * lines.length;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.58)';
    ctx.fillRect(0, height - boxHeight, width, boxHeight);
    ctx.fillStyle = '#fff';
    ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif`;
    ctx.textBaseline = 'top';
    ctx.shadowColor = 'rgba(0,0,0,.65)';
    ctx.shadowBlur = 3;
    lines.forEach((line, index) => {
      ctx.fillText(line, padding, height - boxHeight + padding + index * lineHeight, width - padding * 2);
    });

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    if (!blob) throw new Error('照片水印生成失败');
    return new File([blob], `task-${Date.now()}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  };

  const handleDirectCapture = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setPhotoProcessing(true);
    try {
      const stampedFile = await watermarkTaskPhoto(file, captureTaskRef.current);
      setImage(stampedFile);
      setPreview(URL.createObjectURL(stampedFile));
      setShowModal('complete');
    } catch (err) {
      console.error(err);
      alert(err.message || '照片处理失败，请重新拍照');
    } finally {
      setPhotoProcessing(false);
    }
  };

  const openComplete = (task) => {
    captureTaskRef.current = task;
    setSelected(task);
    setForm({});
    setImage(null);
    setPreview(null);
    setShowModal(null);
    if (captureInputRef.current) captureInputRef.current.click();
  };

  const openAddTemplate = () => {
    setSelected(null);
    setForm({ assign_type: 'department', department: '', scheduled_time: '08:00', scheduled_time_end: '', checklist_template_id: '' });
    setSelectedWeekdays([1,2,3,4,5]);
    setTaskType('普通任务');
    setInspectLocation('');
    setInspectItems('');
    setShowModal('add_template');
  };

  const openEditTemplate = (tpl) => {
    setSelected(tpl);
    const isInspect = tpl.task_type === '巡检' || tpl.title.startsWith('巡检 - ');
    setTaskType(isInspect ? '巡检' : '普通任务');
    setInspectLocation(isInspect ? (tpl.inspect_location || tpl.title.replace('巡检 - ', '')) : '');
    setInspectItems(isInspect ? (tpl.inspect_items || '') : '');
    setForm({ title: isInspect ? '' : tpl.title, description: tpl.description||'', scheduled_time: tpl.scheduled_time, scheduled_time_end: tpl.scheduled_time_end||'', assign_type: tpl.assign_type, department: tpl.department||'', assign_user_id: tpl.assign_user_id||'', checklist_template_id: tpl.checklist_template_id||'' });
    setSelectedWeekdays(tpl.weekdays.split(',').map(Number));
    setShowModal('add_template');
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('确定删除此任务模板？')) return;
    try {
      await API.delete(`/daily/templates/${id}`);
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err) { alert('删除失败'); }
  };

  const isOverdue = (task) => {
    if (task.status !== '待完成') return false;
    const today = getLocalDateStr();
    if (task.task_date !== today) return false;
    const now = new Date();
    const endTime = task.scheduled_time_end || task.scheduled_time;
    if (!endTime) return false;
    const [h, m] = String(endTime).split(':').map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return false;
    return now.getHours() > h || (now.getHours() === h && now.getMinutes() > m);
  };

  // 任务计划时间前30分钟开始允许执行
  const isExecutionTimeReached = (task) => {
    if (!task.task_date || !task.scheduled_time) return true;

    const [year, month, day] = String(task.task_date).substring(0, 10).split('-').map(Number);
    const [hour, minute] = String(task.scheduled_time).split(':').map(Number);

    const taskTime = new Date(year, month - 1, day, hour, minute, 0);
    const allowFrom = new Date(taskTime.getTime() - 30 * 60 * 1000);

    return new Date() >= allowFrom;
  };

  const getExecutionOpenTime = (task) => {
    if (!task.scheduled_time) return '';
    const [hour, minute] = String(task.scheduled_time).split(':').map(Number);
    const d = new Date(2000, 0, 1, hour, minute, 0);
    d.setMinutes(d.getMinutes() - 30);
    return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  };

  const canComplete = (task) => {
    if (task.status === '已完成') return false;
    if (!isExecutionTimeReached(task)) return false;
    const role = user.role;
    if (['admin','manager','general_manager'].includes(role)) return true;
    if (task.assign_type === 'department') return departmentMatches(role, task.department);
    if (task.assign_type === 'user') return task.assign_user_id === user.id;
    return false;
  };

  const pendingCount = tasks.filter(t => t.status === '待完成' && isOverdue(t)).length;
  const getAssignLabel = (t) => t.assign_type === 'department' ? t.department : (t.assign_user_name || '未知');

  return (
    <div style={styles.container}>
      <input ref={captureInputRef} type="file" accept="image/*" capture="environment"
        style={{display:'none'}} onChange={handleDirectCapture} />
      {photoProcessing && (
        <div style={{position:'fixed', inset:0, zIndex:2000, background:'rgba(0,0,0,.55)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'18px', fontWeight:700}}>
          正在写入时间和位置水印…
        </div>
      )}
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>
          每日任务 {pendingCount > 0 && <span style={styles.badge}>{pendingCount}</span>}
        </h1>
        {isManager && (
          <div style={{display:'flex', gap:'8px'}}>
            <button style={styles.addBtn} onClick={() => setTab(tab==='template' ? 'today' : 'template')}>⚙️ 任务模板</button>
            {tab === 'template' && <button style={{...styles.addBtn, background:'rgba(255,255,255,0.3)'}} onClick={openAddTemplate}>+ 新增</button>}
          </div>
        )}
      </div>

      <div style={styles.tabs}>
        <button style={{...styles.tab, ...(tab==='today' ? styles.tabActive : {})}} onClick={() => setTab('today')}>📋 今日任务</button>
        {canViewHistory && (
          <button style={{...styles.tab, ...(tab==='history' ? styles.tabActive : {})}}
            onClick={() => setTab('history')}>
            🕘 完成记录
          </button>
        )}
        {isManager && <button style={{...styles.tab, ...(tab==='checklist' ? styles.tabActive : {})}} onClick={() => setTab('checklist')}>🔍 巡检</button>}
      </div>

      {tab === 'today' && (
        <>
          <div style={styles.dateBar}>
            <input type="date" style={styles.dateInput} value={selectedDate} onChange={e => setSelectedDate(e.target.value)} />
            <span style={styles.dateLabel}>{selectedDate === getLocalDateStr() ? '今天' : ''}</span>
          </div>
          <div style={styles.content}>
            {loading ? <div style={styles.loading}>加载中...</div>
              : tasks.length === 0 ? <div style={styles.empty}>今日暂无任务</div>
              : tasks.map(task => {
                const overdue = isOverdue(task);
                const status = overdue && task.status === '待完成' ? '已超时' : task.status;
                const isInspect = task.title.startsWith('巡检 - ');
                return (
                  <div key={task.id} style={{...styles.taskCard, borderLeftColor: STATUS_COLORS[status], background: status==='已超时'?'#fff2f0':'#fff'}}>
                    <div style={styles.taskTop}>
                      <span style={{...styles.statusBadge, background: STATUS_COLORS[status]}}>{status}</span>
                      {isInspect && <span style={styles.inspectBadge}>🔍 巡检</span>}
                      <span style={styles.timeLabel}>🕐 {task.scheduled_time}</span>
                      <span style={styles.assignLabel}>👤 {getAssignLabel(task)}</span>
                    </div>
                    <div style={styles.taskTitle}>{task.title}</div>
                    {task.status === '已完成' && (
                      <div style={styles.completedInfo}>
                        ✅ {task.completed_by_name} 于 {new Date(task.completed_at).toLocaleTimeString('zh-CN',{hour:'2-digit',minute:'2-digit'})} 完成
                        {task.image_path && <img src={`/uploads/${task.image_path}`} style={styles.thumbImg} alt="" onClick={() => window.open(`/uploads/${task.image_path}`)} />}
                      </div>
                    )}
                    {task.status !== '已完成' && !isExecutionTimeReached(task) && (
                      <button
                        disabled
                        style={{
                          ...styles.completeBtn,
                          background:'#d9d9d9',
                          color:'#666',
                          cursor:'not-allowed',
                          boxShadow:'none'
                        }}>
                        🔒 {getExecutionOpenTime(task)} 后可执行
                      </button>
                    )}

                    {canComplete(task) && (
                      task.task_type === '巡检' && task.checklist_template_id
                        ? <button style={{...styles.completeBtn, background:'linear-gradient(90deg,#1a6a3a,#2d9d5a)'}}
                            onClick={() => navigate(`/checklist/execute/${task.id}?templateId=${task.checklist_template_id}`)}>
                            🔍 开始巡检
                          </button>
                        : <button style={styles.completeBtn} onClick={() => openComplete(task)}>📷 拍照并完成</button>
                    )}
                    {user.role === 'admin' && (
                      <button style={styles.deleteTaskBtn} onClick={async () => {
                        if (!window.confirm('确定删除此任务记录？')) return;
                        try { await API.delete('/daily/' + task.id); fetchTasks(); }
                        catch(e) { alert('删除失败'); }
                      }}>🗑️ 删除</button>
                    )}
                  </div>
                );
              })}
          </div>
        </>
      )}

      {tab === 'history' && canViewHistory && (
        <>
          <div style={styles.dateBar}>
            <input
              type="date"
              style={styles.dateInput}
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
            />
            <span style={styles.dateLabel}>
              {selectedDate === getLocalDateStr() ? '今天的完成记录' : '所选日期的完成记录'}
            </span>
          </div>

          <div style={styles.content}>
            {loading ? (
              <div style={styles.loading}>加载中...</div>
            ) : history.length === 0 ? (
              <div style={styles.empty}>这一天暂无已完成记录</div>
            ) : history.map(record => {
              const isChecklist = record.task_type === '巡检' ||
                (record.checklist_items && record.checklist_items.length > 0);
              const finishTime = record.completed_at_actual || record.completed_at;

              return (
                <div key={record.id} style={{
                  ...styles.taskCard,
                  borderLeftColor: isChecklist ? '#1a6a3a' : '#52c41a'
                }}>
                  <div style={styles.taskTop}>
                    <span style={{
                      ...styles.statusBadge,
                      background: isChecklist ? '#1a6a3a' : '#52c41a'
                    }}>
                      {isChecklist ? '巡检' : '日常'}
                    </span>
                    <span style={styles.timeLabel}>
                      🕐 {finishTime
                        ? new Date(finishTime).toLocaleString('zh-CN', {
                            month:'2-digit',
                            day:'2-digit',
                            hour:'2-digit',
                            minute:'2-digit'
                          })
                        : '时间未知'}
                    </span>
                  </div>

                  <div style={styles.taskTitle}>{record.title}</div>

                  <div style={{
                    marginTop:'8px',
                    color:'#555',
                    fontSize:'13px',
                    lineHeight:1.8
                  }}>
                    <div>👤 执行人：{record.completed_by_name || '未知'}</div>
                    {record.department && <div>🏢 部门：{record.department}</div>}
                    {record.inspect_location && <div>📍 地点：{record.inspect_location}</div>}
                    {record.description && <div>📋 内容：{record.description}</div>}
                    {record.note && <div>📝 完成说明：{record.note}</div>}
                  </div>

                  {record.image_path && (
                    <div style={{marginTop:'10px'}}>
                      <img
                        src={`/uploads/${record.image_path}`}
                        style={{
                          width:'100px',
                          height:'100px',
                          objectFit:'cover',
                          borderRadius:'8px',
                          cursor:'pointer',
                          border:'1px solid #eee'
                        }}
                        alt="任务完成照片"
                        onClick={() => window.open(`/uploads/${record.image_path}`)}
                      />
                    </div>
                  )}

                  {isChecklist && record.checklist_items && record.checklist_items.length > 0 && (
                    <div style={{
                      marginTop:'12px',
                      borderTop:'1px solid #eee',
                      paddingTop:'10px'
                    }}>
                      <div style={{
                        fontWeight:700,
                        color:'#1a6a3a',
                        marginBottom:'8px'
                      }}>
                        巡检项目（{record.checklist_items.length}项）
                      </div>

                      {record.checklist_items.map((item, index) => (
                        <div key={item.id} style={{
                          background:item.is_ok === 1 ? '#f6ffed' : '#fff2f0',
                          border:`1px solid ${item.is_ok === 1 ? '#b7eb8f' : '#ffa39e'}`,
                          borderRadius:'8px',
                          padding:'10px',
                          marginBottom:'8px'
                        }}>
                          <div style={{
                            fontSize:'14px',
                            fontWeight:600,
                            color:'#333'
                          }}>
                            {index + 1}. {item.item_name}
                            <span style={{
                              float:'right',
                              color:item.is_ok === 1 ? '#389e0d' : '#cf1322'
                            }}>
                              {item.is_ok === 1 ? '✓ 正常' : '✗ 异常'}
                            </span>
                          </div>

                          {item.note && (
                            <div style={{fontSize:'13px', color:'#666', marginTop:'6px'}}>
                              说明：{item.note}
                            </div>
                          )}

                          {item.image_path && (
                            <img
                              src={`/uploads/${item.image_path}`}
                              style={{
                                width:'88px',
                                height:'88px',
                                objectFit:'cover',
                                borderRadius:'8px',
                                marginTop:'8px',
                                cursor:'pointer'
                              }}
                              alt={item.item_name}
                              onClick={() => window.open(`/uploads/${item.image_path}`)}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === 'template' && (
        <div style={styles.content}>
          {loading ? <div style={styles.loading}>加载中...</div>
            : templates.length === 0 ? <div style={styles.empty}>暂无任务模板</div>
            : templates.map(tpl => {
              const isInspect = tpl.title.startsWith('巡检 - ');
              return (
                <div key={tpl.id} style={{...styles.tplCard, opacity: tpl.is_active ? 1 : 0.5}}>
                  <div style={styles.tplTop}>
                    <span style={styles.tplTitle}>
                      {isInspect && '🔍 '}{tpl.title}
                    </span>
                    <span style={styles.tplTime}>🕐 {tpl.scheduled_time}</span>
                  </div>
                  <div style={styles.tplInfo}>
                    <span>👤 {tpl.assign_type==='department' ? tpl.department : tpl.assign_user_name}</span>
                    <span>📅 {tpl.weekdays.split(',').map(d => WEEKDAYS.find(w => w.value===parseInt(d))?.label).join(' ')}</span>
                  </div>
                  <div style={styles.tplActions}>
                    <button style={styles.editBtn} onClick={() => openEditTemplate(tpl)}>编辑</button>
                    <button style={{...styles.editBtn, background:'#ff4d4f', color:'#fff'}} onClick={() => handleDeleteTemplate(tpl.id)}>删除</button>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {/* 完成确认弹窗 */}
      {showModal === 'complete' && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>确认完成：{selected?.title}</h3>
            <div style={styles.photoTip}>📷 请拍照上传完成凭证</div>
            <div style={styles.uploadArea}>
              {preview ? <img src={preview} style={styles.previewImg} alt="已添加时间和位置水印" /> : (
                <><span style={{fontSize:'36px'}}>📷</span><span style={{color:'#888',fontSize:'13px'}}>等待拍照</span></>
              )}
            </div>
            <button type="button" style={{...styles.cancelBtn, width:'100%', marginBottom:'12px'}}
              onClick={() => captureInputRef.current && captureInputRef.current.click()}>
              📷 重新拍照
            </button>
            <CnInput style={styles.minput} placeholder="备注（可选）" value={form.note||''} onChange={v => setForm({...form, note:v})} />
            <div style={styles.modalBtns}>
              <button style={styles.cancelBtn} onClick={() => setShowModal(null)}>取消</button>
              <button style={styles.confirmBtn} onClick={handleComplete}>确认完成</button>
            </div>
          </div>
        </div>
      )}

      {/* 巡检清单Tab */}
      {tab === 'checklist' && isManager && (
        <ChecklistAdmin user={user} embedded={true} />
      )}

      {/* 新增/编辑模板弹窗 */}
      {showModal === 'add_template' && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>{selected ? '编辑任务模板' : '新增任务模板'}</h3>
            <div style={styles.fieldLabel}>任务类型</div>
            <div style={styles.radioGroup}>
              <button style={{...styles.radioBtn, ...(taskType==='普通任务' ? styles.radioBtnActive : {})}}
                onClick={() => { setTaskType('普通任务'); setForm({...form, checklist_template_id:''}); }}>📋 普通任务</button>
              <button style={{...styles.radioBtn, ...(taskType==='巡检' ? styles.radioBtnActive : {})}}
                onClick={() => { setTaskType('巡检'); }}>🔍 巡检</button>
            </div>
            {taskType === '普通任务' && (
              <CnInput style={styles.minput} placeholder="任务名称 *" value={form.title||''} onChange={v => setForm({...form, title:v})} />
            )}

            {taskType === '巡检' && (
              <>
                <div style={styles.fieldLabel}>选择巡检清单模板 *</div>
                <select style={styles.minput} value={form.checklist_template_id||''} onChange={e => {
                  const tid = e.target.value;
                  const tpl = checklistTemplates.find(t => String(t.id) === String(tid));
                  setForm({...form, checklist_template_id: tid});
                  setInspectLocation(tpl ? tpl.location : '');
                }}>
                  <option value="">请选择模板 *</option>
                  {checklistTemplates.map(t => <option key={t.id} value={t.id}>{t.name} · {t.location}</option>)}
                </select>
                {form.checklist_template_id && (() => {
                  const tpl = checklistTemplates.find(t => String(t.id) === String(form.checklist_template_id));
                  return tpl ? (
                    <div style={{background:'#f6ffed', border:'1px solid #b7eb8f', borderRadius:'8px', padding:'10px 12px', fontSize:'13px', color:'#389e0d', marginTop:'6px'}}>
                      📍 地点：{tpl.location} · 共 {tpl.item_count} 个巡检项目<br/>
                      <span style={{color:'#888', fontSize:'12px'}}>异常自动派单给：{tpl.auto_task_department}</span>
                    </div>
                  ) : null;
                })()}
              </>
            )}

            <div style={styles.fieldLabel}>{taskType === '巡检' ? '执行时间段 *' : '执行时间 *'}</div>
            <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
              <input type="time" style={{...styles.minput, flex:1}} value={form.scheduled_time||''} onChange={e => setForm({...form, scheduled_time:e.target.value})} />
              {taskType === '巡检' && (
                <>
                  <span style={{color:'#999'}}>至</span>
                  <input type="time" style={{...styles.minput, flex:1}} value={form.scheduled_time_end||''} onChange={e => setForm({...form, scheduled_time_end:e.target.value})} />
                </>
              )}
            </div>

            <div style={styles.fieldLabel}>执行日期 *</div>
            <div style={styles.weekdayRow}>
              {WEEKDAYS.map(d => (
                <button key={d.value} style={{...styles.wdBtn, ...(selectedWeekdays.includes(d.value) ? styles.wdBtnActive : {})}}
                  onClick={() => toggleWeekday(d.value)}>{d.label}</button>
              ))}
            </div>
            <div style={styles.weekdayShortcuts}>
              <button style={styles.shortcutBtn} onClick={() => setSelectedWeekdays([1,2,3,4,5])}>工作日</button>
              <button style={styles.shortcutBtn} onClick={() => setSelectedWeekdays([6,7])}>周末</button>
              <button style={styles.shortcutBtn} onClick={() => setSelectedWeekdays([1,2,3,4,5,6,7])}>每天</button>
            </div>

            <div style={styles.fieldLabel}>分派方式 *</div>
            <div style={styles.radioGroup}>
              {[['department','分派到部门'],['user','分派到个人']].map(([v,l]) => (
                <button key={v} style={{...styles.radioBtn, ...(form.assign_type===v ? styles.radioBtnActive : {})}}
                  onClick={() => setForm({...form, assign_type:v, department:'', assign_user_id:''})}>{l}</button>
              ))}
            </div>
            {form.assign_type === 'department' && (
              <select style={styles.minput} value={form.department||''} onChange={e => setForm({...form, department:e.target.value})}>
                <option value="">选择部门 *</option>
                {DEPARTMENTS.map(dept => <option key={dept} value={dept}>{dept}</option>)}
              </select>
            )}
            {form.assign_type === 'user' && (
              <select style={styles.minput} value={form.assign_user_id||''} onChange={e => setForm({...form, assign_user_id:e.target.value})}>
                <option value="">选择负责人 *</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.real_name}</option>)}
              </select>
            )}

            <div style={styles.modalBtns}>
              <button style={styles.cancelBtn} onClick={() => setShowModal(null)}>取消</button>
              <button style={styles.confirmBtn} onClick={handleAddTemplate}>{selected ? '保存修改' : '创建'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { minHeight:'100vh', background:'#f5f7fa', paddingBottom:'30px' },
  header: { background:'linear-gradient(90deg, #d46b08, #fa8c16)', padding:'16px', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, zIndex:100 },
  headerTitle: { color:'#fff', margin:0, fontSize:'18px', fontWeight:700, display:'flex', alignItems:'center', gap:'8px' },
  badge: { background:'#ff4d4f', color:'#fff', borderRadius:'10px', padding:'1px 8px', fontSize:'13px' },
  addBtn: { background:'#fff', color:'#d46b08', border:'none', borderRadius:'20px', padding:'8px 18px', fontSize:'15px', fontWeight:600, cursor:'pointer' },
  tabs: { display:'flex', background:'#fff', borderBottom:'2px solid #f0f0f0' },
  tab: { flex:1, padding:'12px', border:'none', background:'transparent', fontSize:'14px', cursor:'pointer', color:'#888' },
  tabActive: { color:'#d46b08', borderBottom:'2px solid #d46b08', fontWeight:700 },
  dateBar: { display:'flex', alignItems:'center', gap:'10px', padding:'10px 16px', background:'#fff', borderBottom:'1px solid #f0f0f0' },
  dateInput: { padding:'6px 10px', border:'1.5px solid #e0e0e0', borderRadius:'8px', fontSize:'14px' },
  dateLabel: { fontSize:'13px', color:'#fa8c16', fontWeight:600 },
  content: { padding:'12px 16px' },
  loading: { textAlign:'center', padding:'40px', color:'#888' },
  empty: { textAlign:'center', padding:'60px', color:'#bbb' },
  taskCard: { borderRadius:'12px', padding:'14px', marginBottom:'10px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', borderLeft:'4px solid #ccc' },
  taskTop: { display:'flex', gap:'8px', alignItems:'center', marginBottom:'8px', flexWrap:'wrap' },
  statusBadge: { color:'#fff', borderRadius:'4px', padding:'2px 8px', fontSize:'12px', fontWeight:600 },
  inspectBadge: { background:'#e6f7ff', color:'#1890ff', borderRadius:'4px', padding:'2px 8px', fontSize:'12px' },
  timeLabel: { fontSize:'13px', color:'#666' },
  assignLabel: { fontSize:'13px', color:'#666' },
  taskTitle: { fontSize:'16px', fontWeight:700, color:'#222', marginBottom:'4px' },
  completedInfo: { fontSize:'13px', color:'#52c41a', marginBottom:'8px', display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' },
  thumbImg: { width:'50px', height:'50px', objectFit:'cover', borderRadius:'6px', cursor:'pointer' },
  completeBtn: { width:'100%', padding:'10px', background:'linear-gradient(90deg, #d46b08, #fa8c16)', color:'#fff', border:'none', borderRadius:'8px', fontSize:'15px', fontWeight:600, cursor:'pointer', marginTop:'8px' },
  tplCard: { background:'#fff', borderRadius:'12px', padding:'14px', marginBottom:'10px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)' },
  tplTop: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' },
  tplTitle: { fontSize:'16px', fontWeight:700, color:'#222' },
  tplTime: { fontSize:'14px', color:'#fa8c16', fontWeight:600 },
  tplInfo: { display:'flex', gap:'12px', fontSize:'13px', color:'#666', marginBottom:'4px' },
  tplActions: { display:'flex', gap:'8px', marginTop:'10px' },
  editBtn: { padding:'6px 16px', background:'#f5f5f5', color:'#333', border:'none', borderRadius:'6px', fontSize:'13px', cursor:'pointer' },
  modal: { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'flex-end', zIndex:200 },
  modalContent: { background:'#fff', width:'100%', borderRadius:'16px 16px 0 0', padding:'24px 20px', maxHeight:'90vh', overflowY:'auto' },
  modalTitle: { margin:'0 0 16px', fontSize:'18px', fontWeight:700, color:'#d46b08' },
  photoTip: { background:'#fff7e6', border:'1px solid #ffd591', borderRadius:'8px', padding:'8px 12px', fontSize:'13px', color:'#d46b08', marginBottom:'10px' },
  uploadArea: { display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', border:'2px dashed #e0e0e0', borderRadius:'12px', padding:'16px', marginBottom:'10px', cursor:'pointer', minHeight:'100px' },
  previewImg: { width:'100%', maxHeight:'180px', objectFit:'cover', borderRadius:'8px' },
  minput: { display:'block', width:'100%', padding:'11px 12px', border:'1.5px solid #e0e0e0', borderRadius:'8px', fontSize:'15px', marginBottom:'10px', boxSizing:'border-box' },
  fieldLabel: { fontSize:'14px', fontWeight:600, color:'#333', marginBottom:'6px' },
  weekdayRow: { display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'8px' },
  wdBtn: { padding:'7px 12px', border:'1.5px solid #e0e0e0', borderRadius:'16px', background:'#fff', fontSize:'13px', cursor:'pointer' },
  wdBtnActive: { background:'#fa8c16', color:'#fff', borderColor:'#fa8c16' },
  weekdayShortcuts: { display:'flex', gap:'8px', marginBottom:'12px' },
  shortcutBtn: { padding:'5px 12px', border:'1px solid #d46b08', borderRadius:'12px', background:'#fff7e6', color:'#d46b08', fontSize:'12px', cursor:'pointer' },
  radioGroup: { display:'flex', flexWrap:'wrap', gap:'8px', marginBottom:'10px' },
  radioBtn: { padding:'8px 14px', border:'1.5px solid #e0e0e0', borderRadius:'20px', background:'#fff', fontSize:'13px', cursor:'pointer' },
  radioBtnActive: { background:'#fa8c16', color:'#fff', borderColor:'#fa8c16' },
  modalBtns: { display:'flex', gap:'10px', marginTop:'8px' },
  cancelBtn: { flex:1, padding:'12px', border:'1.5px solid #e0e0e0', borderRadius:'8px', fontSize:'16px', background:'#fff', cursor:'pointer' },
  confirmBtn: { flex:1, padding:'12px', background:'#fa8c16', color:'#fff', border:'none', borderRadius:'8px', fontSize:'16px', fontWeight:600, cursor:'pointer' }
};
